import {
  EducationControllerService,
  EducationJourneyProgramStatusResponse,
  SubscriptionDetailsResponse,
  TaskResponse,
  UpdateTaskRequest
} from "@9amhealth/openapi";
import { Blac, Cubit } from "blac-next";
import type { JourneyContentResponse } from "@9amhealth/cms-api";
import { AppRemoteConfigCubit } from "src/state/AppRemoteConfigCubit/AppRemoteConfigCubit";
import { KnownProgram } from "src/constants/programs";
import {
  programState,
  subscriptionState,
  tracker,
  userState
} from "src/state/state";
import { SUBSCRIPTION_PLAN_ID_CDCDPP } from "src/constants/misc";
import TaskManagementBloc, {
  LoadProgramTasksOptions,
  TaskKey,
  TaskObserverEvent,
  TaskResponseKnown
} from "src/state/TaskManagementBloc/TaskManagementBloc";
import {
  ColorScheme,
  moduleNameToSchemaMap
} from "components/UserJourneyOverview/lib";
import { mark } from "src/lib/learnDiagnostics/timeline";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { ProfileProgramMembership } from "src/constants/programs";
import translate from "src/lib/translate";
import { createAppPath } from "src/lib/createAppPath";
import Path from "src/constants/path";
import { DbJourneyStatusService } from "src/lib/db/DbJourneyStatusService";
import { JourneyMetaService } from "src/lib/db/JourneyMetaService";
import { UserJourneyHydrationCache } from "src/lib/db/UserJourneyHydrationCache";
import { LearnPageViewConfigHydrationCache } from "src/lib/db/LearnPageViewConfigHydrationCache";
import { onAppActive } from "src/hybrid/appLifecycle";
import { globalEvents } from "src/constants/globalEvents";
import WebSocketBloc, {
  WebsocketMessageType
} from "src/state/WebSocketBloc/WebSocketBloc";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import { onUserSessionReset } from "src/lib/onUserSessionReset";

const JOURNEY_STATUSES_META_KEY = "statuses";
const JOURNEY_STATUSES_TTL_MS = 5 * 60 * 1000;

export type JourneyType =
  | "mindset-journey"
  | "physical-activity-journey"
  | "nutrition-journey";

export enum Journey {
  MINDSET = "mindset-journey",
  PHYSICAL_ACTIVITY = "physical-activity-journey",
  NUTRITION = "nutrition-journey"
}

export interface UserJourneyLessonItem {
  type: "typeform" | "article" | "coaching";
  typeformId?: string;
  url?: string;
  task?: TaskResponseKnown;
}

export interface UserJourneyLesson {
  name: string;
  taskGroup?: string;
  items: UserJourneyLessonItem[];
}

export interface UserJourneyModule {
  name: string;
  theme: ColorScheme;
  subtitle: string;
  group: string;
  lessons: UserJourneyLesson[];
  index: number;
  totalModules: number;
}

export type JourneyViewType = "cdcdpp" | "journey-overview";

export interface LearnPageViewConfig {
  viewType: JourneyViewType;
  program: KnownProgram;
}

const getTasksFromHydratedModules = (
  modules?: UserJourneyModule[]
): TaskResponseKnown[] => {
  if (!modules) {
    return [];
  }

  const seenTaskKeys = new Set<string>();

  return modules.flatMap((module) =>
    module.lessons.flatMap((lesson) =>
      lesson.items.flatMap((item) => {
        if (!item.task) {
          return [];
        }

        const taskKey = `${item.task.program}-${item.task.group}-${item.task.slug}`;
        if (seenTaskKeys.has(taskKey)) {
          return [];
        }

        seenTaskKeys.add(taskKey);
        return [item.task];
      })
    )
  );
};

export interface UserJourneyState {
  enableAll: boolean;
  modules?: UserJourneyModule[];
  name?: string;
  loading?: boolean;
  journeys: EducationJourneyProgramStatusResponse[];
  journeyCardsVisible?: boolean;
  journeyCardsLocked?: boolean;
  viewType?: JourneyViewType;
  viewTypeLoading?: boolean;
}

interface UserJourneyProps {
  journey: string;
  taskProgram: string;
}

export default class UserJourneyBloc extends Cubit<
  UserJourneyState,
  UserJourneyProps
> {
  tasks: TaskResponseKnown[] = [];
  journeyContents: Map<
    string,
    JourneyContentResponse & { programKey: string }
  > = new Map();
  private readonly JourneyStatus =
    EducationJourneyProgramStatusResponse.journeyStatus;

  static getProgramFromMembership = (): KnownProgram | null => {
    if (!userState.programMemberships) {
      return null;
    }

    const activeProgram = userState.programMemberships.find(
      (membership) =>
        membership.active || (!membership.start && !membership.end)
    )?.program;

    switch (activeProgram) {
      case ProfileProgramMembership.EVERYDAY_DIABETES_JOURNEY:
        return KnownProgram.EVERYDAY_DIABETES_JOURNEY;
      case ProfileProgramMembership.HEALTHY_WEIGHT_JOURNEY:
        return KnownProgram.PATH_HEALTHY_WEIGHT_HWJ_12W;
      default:
        return KnownProgram.PATH_HEALTHY_WEIGHT_HWJ_12W;
    }
  };

  static getTitleFromMembership = (): string | null => {
    if (!userState.programMemberships) {
      return null;
    }

    const activeProgram = userState.programMemberships.find(
      (membership) =>
        membership.active || (!membership.start && !membership.end)
    )?.program;

    switch (activeProgram) {
      case ProfileProgramMembership.EVERYDAY_DIABETES_JOURNEY:
        return translate("lifestyle.title_everydayDiabetesJourney");
      case ProfileProgramMembership.HEALTHY_WEIGHT_JOURNEY:
        return translate("lifestyle.title_lifebalance");
      default:
        return translate("lifestyle.title_lifebalance");
    }
  };

  /**
   * Creates a journey path for navigation
   * @param module - The journey module identifier
   * @param slug - Optional task slug within the module
   * @returns The full journey path (e.g., "/app/journey/module-1" or "/app/journey/module-1/task-slug")
   */
  static createJourneyPath = (module: string, slug?: string): string => {
    const basePath = `${createAppPath(Path.journey)}/${module}`;
    return slug ? `${basePath}/${slug}` : basePath;
  };

  static determineViewType(
    subscriptions: SubscriptionDetailsResponse[]
  ): JourneyViewType {
    const hasCdcdppSubscription = subscriptions.some(
      (sub) => sub.planId === SUBSCRIPTION_PLAN_ID_CDCDPP
    );

    if (hasCdcdppSubscription) {
      return "cdcdpp";
    }

    const programFromMembership = UserJourneyBloc.getProgramFromMembership();

    if (
      programFromMembership === KnownProgram.PATH_HEALTHY_WEIGHT_HWJ_12W ||
      programFromMembership === KnownProgram.EVERYDAY_DIABETES_JOURNEY
    ) {
      return "journey-overview";
    }

    return "journey-overview";
  }

  static async loadAndDetermineViewType(): Promise<LearnPageViewConfig> {
    const ownerId = StorageController.activeUserId;
    try {
      await Promise.all([
        subscriptionState.loadAllSubscriptions(),
        programState.updatePrograms({ loadSubscriptions: true }),
        userState.getProfileAttributes()
      ]);

      const allSubscriptions = subscriptionState.state.allSubscriptions ?? [];
      const config: LearnPageViewConfig = {
        viewType: UserJourneyBloc.determineViewType(allSubscriptions),
        program:
          UserJourneyBloc.getProgramFromMembership() ??
          KnownProgram.PATH_HEALTHY_WEIGHT_HWJ_12W
      };
      LearnPageViewConfigHydrationCache.write(config, ownerId);
      return config;
    } catch (error) {
      reportErrorSentry(error as Error);
      return (
        LearnPageViewConfigHydrationCache.read() ?? {
          viewType: "journey-overview",
          program: KnownProgram.PATH_HEALTHY_WEIGHT_HWJ_12W
        }
      );
    }
  }

  private cleanupCallbacks: Array<() => void> = [];
  private taskObserverCleanup: (() => void) | null = null;
  private disposed = false;
  private generation = 0;
  private loadedTaskProgram?: string;
  private journeyStatusRefreshPromise: Promise<void> | null = null;
  private lastRefreshAt = 0;

  constructor(props: UserJourneyProps) {
    const hydrated = props ? UserJourneyHydrationCache.read(props) : null;
    const hydratedTasks = getTasksFromHydratedModules(hydrated?.modules);
    super({
      enableAll: false,
      journeys: hydrated?.journeys ?? [],
      modules: hydrated?.modules,
      loading: hydrated ? false : undefined
    });
    this.tasks = hydratedTasks;
    this.props = props;
    void this.loadContent();
    document.addEventListener(
      globalEvents.QUESTIONNAIRE_SAVED,
      this.handleQuestionnaireSaved
    );
    this.cleanupCallbacks.push(() => {
      document.removeEventListener(
        globalEvents.QUESTIONNAIRE_SAVED,
        this.handleQuestionnaireSaved
      );
    });
    this.cleanupCallbacks.push(onUserSessionReset(this.discardInFlightWork));
    this.cleanupCallbacks.push(onAppActive(this.handleAppResume));
  }

  private handleQuestionnaireSaved = (): void => {
    void this.refreshContent();
  };

  // Returning to a backgrounded app can be hours later. Reuse the task-event
  // debounce so a resume arriving alongside queued websocket events refreshes
  // once, and skip work while the cached data is still within its TTL.
  private handleAppResume = (): void => {
    if (Date.now() - this.lastRefreshAt < JOURNEY_STATUSES_TTL_MS) {
      return;
    }
    this.scheduleDebouncedRefresh();
  };

  /** Makes every load started before this call drop its result. */
  private discardInFlightWork = (): void => {
    this.generation += 1;
    this.journeyStatusRefreshPromise = null;
  };

  private isCurrent = (generation: number): boolean =>
    !this.disposed && generation === this.generation;

  /**
   * The instance is shared, and useBloc swaps its props in place when the
   * learn page resolves a different program, so the loaded journey has to be
   * dropped explicitly or the previous program keeps rendering.
   */
  reloadIfProgramChanged = (): void => {
    if (this.props?.taskProgram === this.loadedTaskProgram) {
      return;
    }
    this.discardInFlightWork();
    this.journeyContents.clear();
    this.tasks = [];
    this.patch({ modules: undefined });
    void this.loadContent();
  };

  // Run cleanups registered during the bloc's lifetime. The bloc is not
  // keepAlive, so a new instance is created on every /journey mount — without
  // this, document listeners and WS observers accumulate across visits.
  _dispose() {
    this.disposed = true;
    if (this.refreshDebounceTimer) {
      clearTimeout(this.refreshDebounceTimer);
      this.refreshDebounceTimer = null;
    }
    this.cleanupCallbacks.forEach((fn) => fn());
    this.cleanupCallbacks = [];
    super._dispose();
  }

  loadContent = async () => {
    if (!this.props?.journey || !this.props?.taskProgram) {
      return;
    }

    const { generation } = this;
    this.lastRefreshAt = Date.now();
    this.loadedTaskProgram = this.props.taskProgram;

    // Hydrated content stays on screen while it is revalidated; only a cold
    // start shows the loader.
    const hasHydratedContent = (this.state.modules?.length ?? 0) > 0;
    const taskLoadOptions: LoadProgramTasksOptions | undefined =
      hasHydratedContent
        ? { forceRefresh: true, seedFromCache: false }
        : undefined;
    if (!hasHydratedContent) {
      this.patch({ loading: true });
    }

    mark("userJourney.loadStart", { hasHydratedContent });
    try {
      await Promise.all([
        this.load12weekJourney(taskLoadOptions),
        this.loadAdvancedJourneysData(taskLoadOptions)
      ]);
      if (this.isCurrent(generation)) {
        this.prepareState();
      }
      mark("userJourney.loadEnd");
    } catch (error) {
      mark("userJourney.loadError", {
        message: error instanceof Error ? error.name : "unknown"
      });
      if (this.isCurrent(generation)) {
        reportErrorSentry(error);
      }
    } finally {
      if (this.isCurrent(generation)) {
        this.patch({ loading: false });
        this.addObserverForTaskChanges();
      }
    }
  };

  /** Reloads in place after a mutation, without showing the loader. */
  refreshContent = async (): Promise<void> => {
    if (!this.props?.journey || !this.props?.taskProgram) {
      return;
    }

    const { generation } = this;
    this.lastRefreshAt = Date.now();

    try {
      await JourneyMetaService.invalidate(JOURNEY_STATUSES_META_KEY);
    } catch (error) {
      reportErrorSentry(error as Error);
    }

    const taskLoadOptions: LoadProgramTasksOptions = {
      forceRefresh: true,
      seedFromCache: false
    };

    try {
      await Promise.all([
        this.load12weekJourney(taskLoadOptions),
        this.loadAdvancedJourneysData(taskLoadOptions)
      ]);
      if (this.isCurrent(generation)) {
        this.prepareState();
      }
    } catch (error) {
      if (this.isCurrent(generation)) {
        reportErrorSentry(error as Error);
      }
    }
  };

  private load12weekJourney = async (
    taskLoadOptions?: LoadProgramTasksOptions
  ) => {
    if (!this.props?.journey || !this.props?.taskProgram) {
      return;
    }

    const { journey, taskProgram } = this.props;

    await Promise.all([
      this.loadJourney(journey),
      this.loadTasksForPrograms([taskProgram as KnownProgram], taskLoadOptions)
    ]);
  };

  private loadAdvancedJourneysData = async (
    taskLoadOptions?: LoadProgramTasksOptions
  ) => {
    const { generation } = this;

    await Promise.all([
      this.getJourneysStatus(),
      this.isChooseNextJourneyAvailable(taskLoadOptions)
    ]);

    const activeJourneyNames = this.getActiveJourneyNames();
    if (!this.isCurrent(generation) || activeJourneyNames.length === 0) {
      return;
    }

    await Promise.all([
      this.loadJourneysContent(activeJourneyNames),
      this.loadTasksForPrograms(activeJourneyNames, taskLoadOptions)
    ]);
  };

  /**
   * Filters out programs that don't match the user's membership.
   * Prevents loading EDJ content/tasks for HWJ users and vice versa.
   */
  private filterProgramsByMembership = async (
    programs: KnownProgram[]
  ): Promise<KnownProgram[]> => {
    // Memberships decide which programs are loadable; an unloaded profile would
    // filter everything out and never retry, leaving the journey empty.
    await userState.ensureProfileAttributesLoaded();
    const currentMembershipProgram = UserJourneyBloc.getProgramFromMembership();

    return programs.filter((program) => {
      if (
        program === KnownProgram.EVERYDAY_DIABETES_JOURNEY &&
        currentMembershipProgram !== KnownProgram.EVERYDAY_DIABETES_JOURNEY
      ) {
        return false;
      }
      if (
        program === KnownProgram.PATH_HEALTHY_WEIGHT_HWJ_12W &&
        currentMembershipProgram !== KnownProgram.PATH_HEALTHY_WEIGHT_HWJ_12W
      ) {
        return false;
      }
      return true;
    });
  };

  private loadJourneysContent = async (journeyNames: KnownProgram[]) => {
    const filteredJourneys =
      await this.filterProgramsByMembership(journeyNames);

    await Promise.all(
      filteredJourneys.map((journeyName) => this.loadJourney(journeyName))
    );
  };

  private loadTasksForPrograms = async (
    programs: KnownProgram[],
    taskLoadOptions?: LoadProgramTasksOptions
  ) => {
    const { generation } = this;
    const filteredPrograms = await this.filterProgramsByMembership(programs);
    const taskManagementState = Blac.getBloc(TaskManagementBloc);
    const allTasks = await Promise.all(
      filteredPrograms.map((program) =>
        taskManagementState.loadProgramTasks(program, taskLoadOptions)
      )
    );

    if (!this.isCurrent(generation)) {
      return;
    }

    // Merge tasks without duplicates
    const newTasks = allTasks.flat().filter(Boolean);
    this.mergeTasks(newTasks);
  };

  private sortByJourneyEnrollmentOrder = (
    programA: string,
    programB: string
  ): number => {
    // If same program, maintain original order
    if (programA === programB) {
      return 0;
    }

    // 12w journey - handle both journey key and task program
    const is12wProgramA =
      programA === this.props?.journey || programA === this.props?.taskProgram;
    const is12wProgramB =
      programB === this.props?.journey || programB === this.props?.taskProgram;

    if (is12wProgramA && !is12wProgramB) {
      return -1;
    }
    if (!is12wProgramA && is12wProgramB) {
      return 1;
    }

    // Sort other programs by enrollment order (updatedAt of enroll task)
    // For journey content programs, map to task program for enroll task lookup
    const getTaskProgramForEnrollLookup = (program: string) => {
      if (program === this.props?.journey) {
        return this.props?.taskProgram;
      }
      return program;
    };

    const enrollTaskA = this.tasks.find(
      (t) =>
        t?.program === getTaskProgramForEnrollLookup(programA) &&
        t?.group === "user-experience" &&
        t?.slug === "enroll"
    );
    const enrollTaskB = this.tasks.find(
      (t) =>
        t?.program === getTaskProgramForEnrollLookup(programB) &&
        t?.group === "user-experience" &&
        t?.slug === "enroll"
    );

    // If either task doesn't have updatedAt, put it at the end
    if (!enrollTaskA?.updatedAt) {
      return 1;
    }
    if (!enrollTaskB?.updatedAt) {
      return -1;
    }

    // Sort by updatedAt (earliest enrolled first)
    return (
      new Date(enrollTaskA.updatedAt).getTime() -
      new Date(enrollTaskB.updatedAt).getTime()
    );
  };

  private mergeTasks = (newTasks: TaskResponseKnown[]) => {
    const tasksByKey = new Map(
      this.tasks.map((task) => [
        `${task.program}-${task.group}-${task.slug}`,
        task
      ])
    );

    newTasks.forEach((task) => {
      tasksByKey.set(`${task.program}-${task.group}-${task.slug}`, task);
    });

    this.tasks = Array.from(tasksByKey.values());

    // Sort all tasks by journey enrollment order
    this.tasks.sort((taskA, taskB) => {
      const programA = taskA?.program;
      const programB = taskB?.program;
      return this.sortByJourneyEnrollmentOrder(programA, programB);
    });
  };

  private getJourneysWithStatuses(
    statuses: EducationJourneyProgramStatusResponse.journeyStatus[]
  ): EducationJourneyProgramStatusResponse[] {
    return this.state.journeys.filter((j) =>
      statuses.includes(j.journeyStatus)
    );
  }

  private getActiveJourneyNames = (): KnownProgram[] => {
    return this.getJourneysWithStatuses([this.JourneyStatus.IN_PROGRESS]).map(
      (j) => j.taskProgramName as KnownProgram
    );
  };

  onTasksChanged = (programs: Map<KnownProgram, TaskResponseKnown[]>): void => {
    if (!this.props?.taskProgram) {
      return;
    }

    const mainProgram = this.props.taskProgram as KnownProgram;
    const activePrograms = this.getActiveJourneyNames();
    const allPrograms = [mainProgram, ...activePrograms];

    const allUpdatedTasks: TaskResponseKnown[] = [];
    allPrograms.forEach((program) => {
      const programTasks = programs.get(program);
      if (programTasks) {
        allUpdatedTasks.push(...programTasks);
      }
    });

    if (allUpdatedTasks.length > 0) {
      this.tasks = allUpdatedTasks;
      // Sort tasks by journey enrollment order to maintain consistent numbering
      this.tasks.sort((taskA, taskB) => {
        const programA = taskA?.program;
        const programB = taskB?.program;
        return this.sortByJourneyEnrollmentOrder(programA, programB);
      });
      this.prepareState();
    }
  };

  addObserverForTaskChanges = (): void => {
    if (this.disposed || this.taskObserverCleanup) {
      return;
    }

    const taskManagementState = Blac.getBloc(TaskManagementBloc);
    this.taskObserverCleanup = taskManagementState.addObserver(
      TaskObserverEvent.TASK_LIST_CHANGED,
      this.onTasksChanged
    );

    // Also trigger a background refresh on WS task events — the backend may have
    // unlocked a dependent task or moved a journey to a new state, and those
    // changes don't always arrive via the task-list observer alone.
    const wsBloc = Blac.getBloc(WebSocketBloc);
    const wsHandles = [
      wsBloc.addObserver(
        WebsocketMessageType.taskStatusUpdated,
        this.handleTaskWsEvent
      ),
      wsBloc.addObserver(
        WebsocketMessageType.taskUpdated,
        this.handleTaskWsEvent
      ),
      wsBloc.addObserver(
        WebsocketMessageType.taskCreated,
        this.handleTaskWsEvent
      )
    ];
    this.cleanupCallbacks.push(() => {
      this.taskObserverCleanup?.();
      this.taskObserverCleanup = null;
      wsHandles.forEach((handle) => handle.remove());
    });
  };

  private refreshDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  private handleTaskWsEvent = (): void => {
    this.scheduleDebouncedRefresh();
  };

  private scheduleDebouncedRefresh = (): void => {
    if (this.refreshDebounceTimer) {
      clearTimeout(this.refreshDebounceTimer);
    }
    this.refreshDebounceTimer = setTimeout(() => {
      this.refreshDebounceTimer = null;
      void this.refreshContent();
    }, 500);
  };

  loadJourney = async (journey: string) => {
    const { generation } = this;
    const response =
      await Blac.getBloc(AppRemoteConfigCubit).loadJourneyContent(journey);
    if (!this.isCurrent(generation)) {
      return;
    }

    this.journeyContents.set(journey, {
      ...response.journey,
      programKey: journey
    });

    // Only update name for the main journey
    if (journey === this.props?.journey) {
      this.patch({ name: response.journey.name });
    }
  };

  prepareState = () => {
    if (this.journeyContents.size === 0) {
      return;
    }

    const sortedJourneyContents = Array.from(
      this.journeyContents.values()
    ).sort((a, b) =>
      this.sortByJourneyEnrollmentOrder(a.programKey, b.programKey)
    );

    const allModules: UserJourneyModule[] = [];
    let globalIndex = 0;
    const tasksByStatus = this.tasks.reduce<Record<string, number>>(
      (acc, t) => ({ ...acc, [t.status]: (acc[t.status] ?? 0) + 1 }),
      {}
    );
    let lessonsBefore = 0;
    let itemsBefore = 0;

    sortedJourneyContents.forEach((journeyContent) => {
      const classes = journeyContent.listOfClassesContentAvailable;

      const journeyModules = classes.map((c) => {
        lessonsBefore += c.listOfLessons.filter(Boolean).length;
        itemsBefore += c.listOfLessons
          .filter(Boolean)
          .reduce((n, l) => n + l.items.length, 0);
        const lessons = c.listOfLessons.filter(Boolean).map((l) => ({
          name: l.name,
          taskGroup: l.taskGroup,
          items: l.items
            .map((i) => ({
              type: i.type,
              typeformId: i.typeformId,
              taskSlug: i.taskSlug,
              url: i.url,
              task: this.tasks.find((t) => {
                return t?.slug === i.taskSlug && t?.group === l.taskGroup;
              })
            }))
            .filter((i) => Boolean(i.task))
        }));

        const theme = (c.colorScheme ??
          moduleNameToSchemaMap.get(c.name)) as ColorScheme;

        return {
          name: c.name,
          theme,
          index: globalIndex++, // Use global index and increment
          totalModules: classes.length,
          subtitle: c.subtitle,
          group: c.taskGroupReference,
          lessons
        };
      });

      allModules.push(...journeyModules);
    });

    mark("userJourney.prepareState", {
      modulesBefore: sortedJourneyContents.reduce(
        (n, j) => n + j.listOfClassesContentAvailable.length,
        0
      ),
      modulesAfter: allModules.length,
      lessonsBefore,
      lessonsAfter: allModules.reduce((n, m) => n + m.lessons.length, 0),
      itemsBefore,
      itemsAfter: allModules.reduce(
        (n, m) => n + m.lessons.reduce((k, l) => k + l.items.length, 0),
        0
      ),
      tasksByStatus: JSON.stringify(tasksByStatus)
    });
    this.patch({ modules: allModules });

    if (this.props?.journey && this.props?.taskProgram) {
      UserJourneyHydrationCache.write({
        journey: this.props.journey,
        taskProgram: this.props.taskProgram,
        modules: allModules,
        journeys: this.state.journeys
      });
    }
  };

  private refreshJourneysStatus = (): Promise<void> => {
    if (!this.journeyStatusRefreshPromise) {
      const request = this.fetchJourneysStatus(this.generation);
      this.journeyStatusRefreshPromise = request;
      void request.finally(() => {
        if (this.journeyStatusRefreshPromise === request) {
          this.journeyStatusRefreshPromise = null;
        }
      });
    }
    return this.journeyStatusRefreshPromise;
  };

  private fetchJourneysStatus = async (generation: number): Promise<void> => {
    const ownerId = StorageController.activeUserId;
    try {
      const response = await EducationControllerService.getEducationJourneys();
      if (!this.isCurrent(generation)) {
        return;
      }
      const journeys = response?.data || [];
      this.patch({ journeys });
      await DbJourneyStatusService.putForOwner(
        ownerId,
        journeys as unknown as Record<string, unknown>[],
        Date.now()
      );
    } catch (error: unknown) {
      if (this.isCurrent(generation)) {
        reportErrorSentry(error as Error);
      }
    }
  };

  /**
   * Background revalidation of cached statuses. When the set of in-progress
   * journeys changed, the journeys that stopped are dropped and the page is
   * reloaded so newly started ones get their content and tasks.
   */
  private revalidateJourneysStatus = (generation: number): void => {
    const previousActiveJourneys = this.getActiveJourneyNames().join();
    void this.refreshJourneysStatus().then(() => {
      if (
        !this.isCurrent(generation) ||
        this.getActiveJourneyNames().join() === previousActiveJourneys
      ) {
        return;
      }
      this.dropInactiveJourneys();
      void this.refreshContent();
    });
  };

  private dropInactiveJourneys = (): void => {
    const activeJourneys = new Set<string>(this.getActiveJourneyNames());
    this.journeyContents.forEach((_content, journeyName) => {
      if (
        journeyName !== this.props?.journey &&
        !activeJourneys.has(journeyName)
      ) {
        this.journeyContents.delete(journeyName);
      }
    });
    this.tasks = this.tasks.filter(
      (task) =>
        task.program === this.props?.taskProgram ||
        activeJourneys.has(task.program)
    );
  };

  getJourneysStatus = async () => {
    const { generation } = this;
    let cached: Record<string, unknown>[] | undefined;
    try {
      cached = await DbJourneyStatusService.get();
    } catch (error: unknown) {
      reportErrorSentry(error);
    }

    if (!this.isCurrent(generation)) {
      return;
    }

    if (!cached) {
      await this.refreshJourneysStatus();
      return;
    }

    this.patch({
      journeys: cached as unknown as EducationJourneyProgramStatusResponse[]
    });

    let stale = true;
    try {
      stale = await JourneyMetaService.isStale(
        JOURNEY_STATUSES_META_KEY,
        JOURNEY_STATUSES_TTL_MS
      );
    } catch (error: unknown) {
      reportErrorSentry(error as Error);
    }
    if (stale && this.isCurrent(generation)) {
      this.revalidateJourneysStatus(generation);
    }
  };

  enrollInJourney = async (
    journey: JourneyType,
    source: "journey-card" | "journey-modal"
  ) => {
    const { generation } = this;
    const ownerId = StorageController.activeUserId;
    this.patch({ loading: true });

    try {
      const taskManagementState = Blac.getBloc(TaskManagementBloc);
      await taskManagementState.updateTaskStatus(
        {
          program: KnownProgram.EDUCATION,
          group: "journeys",
          slug: "choose-next-journey"
        },
        UpdateTaskRequest.status.COMPLETED,
        true //We want to skip the reload of the tasks for the education program, since we need to enroll in the journey first
      );

      // Stop before the enroll write: after a user switch it would enroll the
      // next user in a journey they never picked.
      if (!this.isCurrent(generation)) {
        return;
      }

      // Update task status for the specific journey
      await taskManagementState.updateTaskStatus(
        {
          program: journey,
          group: "user-experience",
          slug: "enroll"
        },
        UpdateTaskRequest.status.COMPLETED
      );

      if (!this.isCurrent(generation)) {
        return;
      }

      const journeysCompleted = this.state.journeys
        .filter((j) => j.journeyStatus === this.JourneyStatus.COMPLETED)
        .map((j) => j.taskProgramName);
      const journeysInProgress = this.state.journeys
        .filter((j) => j.journeyStatus === this.JourneyStatus.IN_PROGRESS)
        .map((j) => j.taskProgramName);
      const journeysAvailable = this.state.journeys
        .filter((j) => j.journeyStatus === this.JourneyStatus.AVAILABLE)
        .map((j) => j.taskProgramName);

      tracker.track("Enroll in Journey", {
        data: {
          journey: journey,
          enrollSource: source,
          journeyStatuses: this.state.journeys.map(
            (j) => `${j.taskProgramName} - ${j.journeyStatus}`
          ),
          journeysCompleted: journeysCompleted,
          journeysInProgress: journeysInProgress,
          journeysAvailable: journeysAvailable,
          noOfJourneysCompleted: journeysCompleted.length,
          noOfJourneysInProgress: journeysInProgress.length,
          noOfJourneysAvailable: journeysAvailable.length
        }
      });

      await DbJourneyStatusService.invalidateForOwner(ownerId);
      if (!this.isCurrent(generation)) {
        return;
      }

      // Reload data to reflect the new enrollment
      await Promise.all([
        this.getJourneysStatus(),
        this.loadAdvancedJourneysData()
      ]);

      if (this.isCurrent(generation)) {
        this.prepareState();
      }
    } catch (error: unknown) {
      if (this.isCurrent(generation)) {
        reportErrorSentry(error);
      }
    } finally {
      if (this.isCurrent(generation)) {
        this.patch({ loading: false });
      }
    }
  };

  isChooseNextJourneyAvailable = async (
    taskLoadOptions?: LoadProgramTasksOptions
  ) => {
    const { generation } = this;
    try {
      const taskManagementState = Blac.getBloc(TaskManagementBloc);
      await taskManagementState.loadProgramTasks(
        KnownProgram.EDUCATION,
        taskLoadOptions
      );
      const task = taskManagementState.getTask(TaskKey.CHOOSE_NEXT_JOURNEY);
      if (this.isCurrent(generation)) {
        this.patch({
          journeyCardsLocked: task?.status !== TaskResponse.status.AVAILABLE
        });
      }
    } catch (error: unknown) {
      if (this.isCurrent(generation)) {
        reportErrorSentry(error);
      }
    }
  };

  private isJourneyCardVisible = (journey: JourneyType) => {
    const isJourneyAvailable = this.getJourneysWithStatuses([
      this.JourneyStatus.AVAILABLE,
      this.JourneyStatus.LOCKED
    ]).some((j) => j.taskProgramName === journey);
    return isJourneyAvailable;
  };

  get isNutritionJourneyCardVisible() {
    return this.isJourneyCardVisible(Journey.NUTRITION);
  }

  get isMindsetJourneyCardVisible() {
    return this.isJourneyCardVisible(Journey.MINDSET);
  }

  get isActivityJourneyCardVisible() {
    return this.isJourneyCardVisible(Journey.PHYSICAL_ACTIVITY);
  }

  get availableJourneysToEnroll() {
    if (this.props?.journey === KnownProgram.EVERYDAY_DIABETES_JOURNEY) {
      return false;
    }

    const allAdvancedJourneys = this.state.journeys.filter(
      (j) => j.taskProgramName !== KnownProgram.EVERYDAY_DIABETES_JOURNEY
    );

    const allJourneysInProgress = allAdvancedJourneys.filter(
      (j) => j.journeyStatus === this.JourneyStatus.IN_PROGRESS
    );

    return !(allJourneysInProgress.length === allAdvancedJourneys.length);
  }

  onTaskStatusUpdated = (
    task: TaskResponseKnown | undefined,
    status: string
  ) => {
    if (!task) {
      return;
    }
    this.tasks = this.tasks.map((t) => {
      if (
        t?.program === task.program &&
        t.group === task.group &&
        t.slug === task.slug
      ) {
        return {
          ...t,
          status
        } as TaskResponseKnown;
      }

      return t;
    });
    this.prepareState();
  };

  isNextAvailableTask = (task: TaskResponseKnown | undefined) => {
    const noAvailableOrInProgressTasks =
      this.tasks.find(
        (task) =>
          task?.status === TaskResponse.status.AVAILABLE ||
          task?.status === TaskResponse.status.IN_PROGRESS
      ) === undefined;
    const completedTask = this.tasks.find(
      (task) => task?.status === TaskResponse.status.COMPLETED
    );
    const firstLockedTask = this.tasks.find(
      (task) => task?.status === TaskResponse.status.LOCKED
    );
    const isLocked = task?.status === TaskResponse.status.LOCKED;

    if (
      isLocked &&
      task.slug === firstLockedTask?.slug &&
      noAvailableOrInProgressTasks &&
      completedTask
    ) {
      return true;
    }
    return false;
  };

  get questionnaireTasks(): TaskResponseKnown[] {
    // Ensure tasks are sorted by journey enrollment order for consistent numbering
    const sortedTasks = [...this.tasks].sort((taskA, taskB) => {
      const programA = taskA?.program;
      const programB = taskB?.program;
      return this.sortByJourneyEnrollmentOrder(programA, programB);
    });

    return sortedTasks.filter((t) => t?.slug.includes("questionnaire"));
  }

  /**
   * The composite key (`${program}-${group}-${slug}`) of the next locked task
   * with a known unlock date, when no task is currently active. Used to
   * auto-scroll the journey view to the upcoming locked task so users can see
   * what's coming next.
   */
  get upcomingLockedTaskSlug(): string | null {
    const hasActiveTask = this.tasks.some(
      (t) =>
        t.status === TaskResponse.status.AVAILABLE ||
        t.status === TaskResponse.status.IN_PROGRESS
    );

    if (hasActiveTask) {
      return null;
    }

    const nextLockedTask = this.tasks.find(
      (t) => t.status === TaskResponse.status.LOCKED && t.availableFrom
    );

    if (!nextLockedTask) {
      return null;
    }

    return `${nextLockedTask.program}-${nextLockedTask.group}-${nextLockedTask.slug}`;
  }
}
